// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DetailPageBody } from '@/components/detail-page-body';
import { DetailPageHeader } from '@/components/detail-page-header';
import { DetailPageHeaderField } from '@/components/detail-page-header-field';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { HorizontalNavigationLink } from '@/components/horizontal-navigation-link';
import { Link } from '@/components/link';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { FulfillmentOrder } from '@/fulfillment/api/types';
import { FulfillmentAttachmentsSection } from '@/fulfillment/components/fulfillment-attachments-section';
import { FulfillmentAuditCard } from '@/fulfillment/components/fulfillment-audit-card';
import { FulfillmentCancelDialog } from '@/fulfillment/components/fulfillment-cancel-dialog';
import { FulfillmentCloseDialog } from '@/fulfillment/components/fulfillment-close-dialog';
import { FulfillmentDetailsSkeleton } from '@/fulfillment/components/fulfillment-details-skeleton';
import { FulfillmentHeaderActions } from '@/fulfillment/components/fulfillment-header-actions';
import { FulfillmentNotesControl } from '@/fulfillment/components/fulfillment-notes-control';
import { FulfillmentOrderDialog } from '@/fulfillment/components/fulfillment-order-dialog';
import { FulfillmentPhaseIndicator } from '@/fulfillment/components/fulfillment-phase-indicator';
import { SendTo3PLDialog } from '@/fulfillment/components/send-to-3pl-dialog';
import { useFulfillment } from '@/fulfillment/hooks/use-fulfillment';
import {
  fulfillmentDetailQueryKey,
  fulfillmentsQueryKey,
} from '@/fulfillment/query-keys';
import { FulfillmentOrderFormData } from '@/fulfillment/schemas/fulfillment-order-form-schema';
import { LocationIntegrationStatus } from '@/location/components/location-integration-status';
import {
  getEffectiveAddress,
  getEffectiveLocationName,
  isEffectiveThirdPartyEnabled,
} from '@/location/utilities/get-effective-third-party';
import { projectFulfillmentBySkuQueryKey } from '@/project/hooks/use-project-fulfillment-by-sku';
import { OutboundShipmentDialog } from '@/shipment/components/outbound-shipment-dialog';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';
import {
  Box,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { Fragment, Suspense, useMemo, useState } from 'react';

export const Route = createFileRoute('/_app/fulfillments/$fulfillmentKey')({
  component: FulfillmentRoute,
});

function FulfillmentRoute() {
  const { fulfillmentKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="fulfillment order"
      listPath="/fulfillments"
      listLabel="Go to fulfillment orders"
    >
      <Suspense fallback={<FulfillmentDetailsSkeleton />}>
        <FulfillmentContent fulfillmentKey={fulfillmentKey} />
      </Suspense>
    </NotFoundErrorBoundary>
  );
}

function FulfillmentContactDisplay(props: {
  contact: FulfillmentOrder['contact'];
  snapshot:
    | NonNullable<
        NonNullable<FulfillmentOrder['orderDataSnapshot']>['contact']
      >['data']
    | null
    | undefined;
}) {
  const name = props.snapshot
    ? [props.snapshot.firstName, props.snapshot.lastName]
        .filter(Boolean)
        .join(' ')
    : props.contact
      ? `${props.contact.firstName} ${props.contact.lastName}`
      : null;
  const company = props.snapshot
    ? props.snapshot.company?.name
    : props.contact?.customer?.name;
  const email = props.snapshot ? props.snapshot.email : props.contact?.email;
  const phone = props.snapshot ? props.snapshot.phone : props.contact?.phone;

  return (
    <Stack direction="row" justifyContent="flex-end">
      <Stack direction="column" alignItems="flex-end" spacing={0.5}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            Shipping contact
          </Typography>
          <Tooltip
            arrow
            title="Contact who will coordinate receiving this shipment"
          >
            <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Tooltip>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto 1fr',
            columnGap: 1,
            rowGap: 0.5,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Name
          </Typography>
          <Typography variant="body2">{name || '—'}</Typography>
          <Typography variant="body2" color="text.secondary">
            Email
          </Typography>
          <Typography variant="body2">{email || '—'}</Typography>
          <Typography variant="body2" color="text.secondary">
            Company
          </Typography>
          <Typography variant="body2">{company || '—'}</Typography>
          <Typography variant="body2" color="text.secondary">
            Phone
          </Typography>
          <Typography variant="body2">{phone || '—'}</Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

function FulfillmentContent(props: { fulfillmentKey: string }) {
  const { fulfillment } = useFulfillment(props.fulfillmentKey);
  const queryClient = useQueryClient();
  const invalidateAfterDriftPull = () => {
    void queryClient.invalidateQueries({ queryKey: fulfillmentsQueryKey });
    void queryClient.invalidateQueries({ queryKey: ['location'] });
  };
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [isShipmentDialogOpen, setIsShipmentDialogOpen] = useState(false);
  const [isSend3PLDialogOpen, setIsSend3PLDialogOpen] = useState(false);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);

  // Build clone form data: pre-populate from location and items only
  const cloneFormData = useMemo((): FulfillmentOrderFormData => {
    return {
      project: null,
      fromLocation: fulfillment.fromLocation,
      toLocation: null,
      promiseShipDate: null,
      promiseDeliveryDate: null,
      orderNotes: null,
      contact: null,
      items: fulfillment.items.map((item) => ({
        item: extractSparseItem(item),
        quantity: item.requestedQuantity,
      })),
    };
  }, [fulfillment]);

  // Handler for opening edit dialog
  const handleOpenEditDialog = () => {
    setIsEditDialogOpen(true);
  };

  // Handler for closing edit dialog
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
  };

  // Handler for opening cancel dialog
  const handleOpenCancelDialog = () => {
    setIsCancelDialogOpen(true);
  };

  // Handler for closing cancel dialog
  const handleCloseCancelDialog = () => {
    setIsCancelDialogOpen(false);
  };

  const handleOpenCloseDialog = () => {
    setIsCloseDialogOpen(true);
  };

  const handleCloseCloseDialog = () => {
    setIsCloseDialogOpen(false);
  };

  // Handler for opening shipment dialog
  const handleOpenShipmentDialog = () => {
    setIsShipmentDialogOpen(true);
  };

  // Handler for closing shipment dialog
  const handleCloseShipmentDialog = () => {
    setIsShipmentDialogOpen(false);
  };

  // Handler for opening send to 3PL dialog
  const handleOpenSend3PLDialog = () => {
    setIsSend3PLDialogOpen(true);
  };

  // Handler for closing send to 3PL dialog
  const handleCloseSend3PLDialog = () => {
    setIsSend3PLDialogOpen(false);
  };

  // Handler for opening clone dialog
  const handleOpenCloneDialog = () => {
    setIsCloneDialogOpen(true);
  };

  // Handler for closing clone dialog
  const handleCloseCloneDialog = () => {
    setIsCloneDialogOpen(false);
  };

  // Handler for successful cancellation
  const handleCancelSuccess = (updatedFulfillment: typeof fulfillment) => {
    // Update the query cache with the cancelled fulfillment
    queryClient.setQueryData(
      fulfillmentDetailQueryKey(props.fulfillmentKey),
      updatedFulfillment
    );
    // Invalidate all fulfillment queries to ensure everything stays in sync
    queryClient.invalidateQueries({
      queryKey: fulfillmentsQueryKey,
      refetchType: 'active',
    });
    // The cancelled FO drops out of the project's by-SKU summary
    // (which excludes cancelled FOs), so refresh that cache too.
    if (updatedFulfillment.project) {
      queryClient.invalidateQueries({
        queryKey: projectFulfillmentBySkuQueryKey(
          updatedFulfillment.project.key
        ),
      });
    }
  };

  // Handler for successful send to 3PL
  const handleSend3PLSuccess = (updatedFulfillment: typeof fulfillment) => {
    // Update the query cache with the updated fulfillment
    queryClient.setQueryData(
      fulfillmentDetailQueryKey(props.fulfillmentKey),
      updatedFulfillment
    );
    // Invalidate all fulfillment queries to ensure everything stays in sync
    queryClient.invalidateQueries({
      queryKey: fulfillmentsQueryKey,
      refetchType: 'active',
    });
  };

  const handleCloseOrderSuccess = (updatedFulfillment: typeof fulfillment) => {
    queryClient.setQueryData(
      fulfillmentDetailQueryKey(props.fulfillmentKey),
      updatedFulfillment
    );
    queryClient.invalidateQueries({
      queryKey: fulfillmentsQueryKey,
      refetchType: 'active',
    });
  };

  return (
    <Container maxWidth="xl">
      <DetailPageHeader
        parentBreadcrumb={
          <Link
            to="/fulfillments"
            variant="body2"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            Fulfillment orders
          </Link>
        }
        currentBreadcrumb={fulfillment.fulfillmentOrderNumber}
        title={fulfillment.fulfillmentOrderNumber}
        titleAdornment={
          fulfillment.exchangeGroup ? (
            <Chip label="Exchange" size="small" />
          ) : null
        }
        actions={
          <FulfillmentHeaderActions
            status={fulfillment.status}
            originLocationIsThirdPartyEnabled={isEffectiveThirdPartyEnabled(
              fulfillment.fromLocation
            )}
            isThirdPartyFulfilled={fulfillment.thirdParty.isEnabled}
            isClosed={fulfillment.closedAt !== null}
            hasZeroQuantityItem={fulfillment.items.some(
              (item) => item.requestedQuantity === 0
            )}
            fulfillmentKey={props.fulfillmentKey}
            fulfillmentNumber={fulfillment.fulfillmentOrderNumber}
            lastSyncedAt={fulfillment.thirdParty.lastSyncedAt}
            lastThirdPartyUpdateAt={fulfillment.thirdParty.lastUpdateAt}
            hasSyncError={fulfillment.thirdParty.hasSyncError}
            syncErrorMessage={fulfillment.thirdParty.syncErrorMessage ?? ''}
            quarantineCount={fulfillment.items.reduce(
              (sum, item) => sum + item.quarantinedQuantity,
              0
            )}
            onEdit={handleOpenEditDialog}
            onCancel={handleOpenCancelDialog}
            onCloseOrder={handleOpenCloseDialog}
            onFulfill={handleOpenShipmentDialog}
            onSendTo3PL={handleOpenSend3PLDialog}
            onClone={handleOpenCloneDialog}
          />
        }
      >
        {fulfillment.exchangeGroup && (
          <Typography variant="body2" color="text.secondary">
            Part of exchange <b>{fulfillment.exchangeGroup.exchangeNumber}</b>
            {fulfillment.exchangeGroup.returnOrders.length > 0 && (
              <>
                {' (linked to '}
                {fulfillment.exchangeGroup.returnOrders.map((ro, index) => (
                  <Fragment key={ro.key}>
                    {index > 0 && ', '}
                    <Link
                      to="/return-orders/$returnOrderKey"
                      params={{ returnOrderKey: ro.key }}
                    >
                      {ro.returnOrderNumber}
                    </Link>
                  </Fragment>
                ))}
                {')'}
              </>
            )}
          </Typography>
        )}
        <DetailPageHeaderField label="Project">
          {fulfillment.project ? (
            <Link
              to="/projects/$projectKey"
              params={{ projectKey: fulfillment.project.key }}
              variant="body2"
            >
              {fulfillment.project.name}
            </Link>
          ) : (
            'Not set'
          )}
        </DetailPageHeaderField>
      </DetailPageHeader>

      {/* Main content */}
      <DetailPageBody
        side={
          <>
            {/* Notes Section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <HardfinTabCardWrapper title="Notes" hideDivider>
                <Box sx={{ height: '12.4rem' }}>
                  <FulfillmentNotesControl
                    fulfillment={fulfillment}
                    isOriginThirdParty={isEffectiveThirdPartyEnabled(
                      fulfillment.fromLocation
                    )}
                  />
                </Box>
              </HardfinTabCardWrapper>
            </Box>

            {/* Attachments Section (files + links) */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FulfillmentAttachmentsSection
                fulfillmentKey={props.fulfillmentKey}
                fulfillmentId={fulfillment.id}
              />
            </Box>

            {/* Audit log */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FulfillmentAuditCard fulfillmentKey={props.fulfillmentKey} />
            </Box>
          </>
        }
      >
        {/* Phase indicator and locations card */}
        <Paper sx={{ minWidth: 0, padding: 3 }} variant="outlined">
          {/* Phase Indicator */}
          <FulfillmentPhaseIndicator
            currentStatus={fulfillment.status}
            originLocationIsThirdPartyEnabled={isEffectiveThirdPartyEnabled(
              fulfillment.fromLocation
            )}
            isThirdPartyFulfilled={fulfillment.thirdParty.isEnabled}
            cancelledReason={fulfillment.cancellationReason}
            createdAt={fulfillment.createdAt}
            openedAt={fulfillment.openedAt}
            shippedAt={fulfillment.mostRecentShippedAt}
            deliveredAt={fulfillment.mostRecentDeliveredAt}
            closedAt={fulfillment.closedAt}
            closedAtSource={fulfillment.closedAtSource}
            closedBy={fulfillment.closedBy}
          />
          {/* From Location */}
          <Box marginTop={4} paddingX={1}>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="column" spacing={0.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">From: </Typography>
                  <Typography variant="body2">
                    {fulfillment.fromLocation
                      ? getEffectiveLocationName(fulfillment.fromLocation)
                      : 'Not set'}
                  </Typography>
                  <LocationIntegrationStatus
                    location={fulfillment.fromLocation}
                    onResolved={invalidateAfterDriftPull}
                  />
                  {isEffectiveThirdPartyEnabled(fulfillment.fromLocation) && (
                    <Chip label="3PL" size="small" />
                  )}
                </Stack>
                {fulfillment.fromLocation &&
                  getEffectiveAddress(fulfillment.fromLocation) && (
                    <Typography variant="body2" color="text.secondary">
                      {getEffectiveAddress(fulfillment.fromLocation)}
                    </Typography>
                  )}
              </Stack>
              <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Promise ship
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    fulfillment.promiseShipDate
                      ? 'text.primary'
                      : 'text.secondary'
                  }
                >
                  {fulfillment.promiseShipDate?.toLocaleString({
                    dateStyle: 'medium',
                  }) || 'Not set'}
                </Typography>
              </Stack>
            </Stack>
          </Box>
          {/* To Location */}
          <Box marginTop={2} paddingX={1}>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="column" spacing={0.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">To: </Typography>
                  <Typography variant="body2">
                    {fulfillment.toLocation
                      ? getEffectiveLocationName(fulfillment.toLocation)
                      : 'Not set'}
                  </Typography>
                  <LocationIntegrationStatus
                    location={fulfillment.toLocation}
                    onResolved={invalidateAfterDriftPull}
                  />
                  {isEffectiveThirdPartyEnabled(fulfillment.toLocation) && (
                    <Chip label="3PL" size="small" />
                  )}
                </Stack>
                {fulfillment.toLocation &&
                  getEffectiveAddress(fulfillment.toLocation) && (
                    <Typography variant="body2" color="text.secondary">
                      {getEffectiveAddress(fulfillment.toLocation)}
                    </Typography>
                  )}
              </Stack>
              <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Promise delivery
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    fulfillment.promiseDeliveryDate
                      ? 'text.primary'
                      : 'text.secondary'
                  }
                >
                  {fulfillment.promiseDeliveryDate?.toLocaleString({
                    dateStyle: 'medium',
                  }) || 'Not set'}
                </Typography>
              </Stack>
            </Stack>
          </Box>
          {/* Contact - shown after locations */}
          {(fulfillment.contact || fulfillment.orderDataSnapshot?.contact) && (
            <Box marginTop={2} paddingX={1}>
              <Divider sx={{ borderStyle: 'dashed', mb: 2 }} />
              <FulfillmentContactDisplay
                contact={fulfillment.contact}
                snapshot={fulfillment.orderDataSnapshot?.contact?.data}
              />
            </Box>
          )}
        </Paper>

        {/* Tabs section */}
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            marginBottom={2}
            sx={{ borderBottom: '1px solid #E0E0E0' }}
            marginX={1}
          >
            <HorizontalNavigationLink
              to="/fulfillments/$fulfillmentKey"
              params={{ fulfillmentKey: props.fulfillmentKey }}
              activeOptions={{ exact: true }}
            >
              Line items
            </HorizontalNavigationLink>
            <HorizontalNavigationLink
              to="/fulfillments/$fulfillmentKey/shipments"
              params={{ fulfillmentKey: props.fulfillmentKey }}
            >
              Shipments
            </HorizontalNavigationLink>
          </Stack>

          <SupportErrorBoundary message="An error occurred.">
            <Outlet />
          </SupportErrorBoundary>
        </Box>
      </DetailPageBody>

      {/* Edit Fulfillment Order Dialog */}
      <FulfillmentOrderDialog
        isOpen={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        fulfillment={fulfillment}
      />

      {/* Clone Fulfillment Order Dialog */}
      <FulfillmentOrderDialog
        isOpen={isCloneDialogOpen}
        onClose={handleCloseCloneDialog}
        initialFormData={cloneFormData}
      />

      {/* Cancel Fulfillment Order Dialog */}
      <FulfillmentCancelDialog
        isOpen={isCancelDialogOpen}
        onClose={handleCloseCancelDialog}
        fulfillmentKey={props.fulfillmentKey}
        foNumber={fulfillment.fulfillmentOrderNumber}
        onSuccess={handleCancelSuccess}
      />

      <FulfillmentCloseDialog
        isOpen={isCloseDialogOpen}
        onClose={handleCloseCloseDialog}
        fulfillmentKey={props.fulfillmentKey}
        foNumber={fulfillment.fulfillmentOrderNumber}
        onSuccess={handleCloseOrderSuccess}
      />

      {/* Send to 3PL Dialog */}
      <SendTo3PLDialog
        isOpen={isSend3PLDialogOpen}
        onClose={handleCloseSend3PLDialog}
        fulfillmentKey={props.fulfillmentKey}
        foNumber={fulfillment.fulfillmentOrderNumber}
        onSuccess={handleSend3PLSuccess}
        toLocationKey={fulfillment.toLocation?.key}
        toLocationName={fulfillment.toLocation?.name}
      />

      {/* Outbound Shipment Dialog */}
      <OutboundShipmentDialog
        isOpen={isShipmentDialogOpen}
        onClose={handleCloseShipmentDialog}
        dialogTitle="Record a fulfillment"
        fulfillmentOrderKey={fulfillment.key}
      />
    </Container>
  );
}
